import { PIPELINE_NODE_CATEGORIES, PIPELINE_NODE_TYPE_NAME } from '@elmes/shared';
import type {
  PipelineNodeKindSpec,
  PlatformExtensionComponentLoader,
  PlatformExtensionConnectorCatalogEntry,
  PlatformExtensionRoute,
  PlatformExtensionSidebarItem,
  PlatformExtensionWeb,
  PlatformExtensionWindowablePage,
} from '@elmes/plugin-sdk';
import { extensions } from 'virtual:platform-extensions';

/**
 * The ONE reader of the build-time web extension slot (vite-plugins/platformExtensions.ts). Core
 * rosters join these lists through `joinCoreRoster`: pageRegistry (components), coreWindowablePages
 * (windowable pages), coreRoutes (routes), pipelineNodeKinds (pipeline node kinds); Sidebar and
 * ConnectionsPage read them as-is. Every
 * refusal throws at module evaluation, which the build runs once in node (WEB_COMPOSITION_MODULES),
 * so a clash refuses the build instead of overriding a core entry — an App.tsx route or a core
 * manifest page (CORE_MANIFEST_PAGE_PATTERNS) alike.
 */
export interface WebExtensionSlotEntry {
  readonly name: string;
  readonly web: PlatformExtensionWeb;
}

export interface ComposedWebExtensions {
  components: Readonly<Record<string, PlatformExtensionComponentLoader>>;
  routes: readonly PlatformExtensionRoute[];
  windowablePages: readonly PlatformExtensionWindowablePage[];
  connectorCatalog: readonly PlatformExtensionConnectorCatalogEntry[];
  sidebar: readonly PlatformExtensionSidebarItem[];
  pipelineNodeKinds: Readonly<Record<string, PipelineNodeKindSpec>>;
}

export class PlatformWebExtensionError extends Error {
  constructor(readonly violations: readonly string[]) {
    super(`web extensions refused: ${violations.join('; ')}`);
    this.name = 'PlatformWebExtensionError';
  }
}

// Record (not a list) so the compiler proves it names every PlatformExtensionWeb field.
const WEB_FIELDS: Readonly<Record<keyof PlatformExtensionWeb, true>> = {
  pages: true,
  sidebar: true,
  components: true,
  windowablePages: true,
  routes: true,
  connectorCatalog: true,
  pipelineNodeKinds: true,
};
// The object-valued fields; every other field is a list.
const RECORD_FIELDS: ReadonlySet<string> = new Set(['components', 'pipelineNodeKinds']);

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const isObject = (value: unknown): boolean => typeof value === 'object' && value !== null && !Array.isArray(value);
// Static only: a `:param` or splat could overlap a core route in ways a match against core cannot see.
const STATIC_ROUTE = /^(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)+$/;
const NODE_KIND_CLASSES = ['color', 'bgColor', 'borderColor'] as const;
const NODE_KIND_FIELDS: ReadonlySet<string> = new Set(['icon', 'label', 'category', ...NODE_KIND_CLASSES, 'hasInput', 'hasOutput']);

/** Why a pipeline node kind spec cannot be bound, or undefined; the icon name resolves where the kinds join core's. */
function nodeKindDefect(spec: unknown): string | undefined {
  if (!isObject(spec)) return 'is not an object';
  const kind = spec as Record<string, unknown>;
  const unknown = Object.keys(kind).filter((key) => !NODE_KIND_FIELDS.has(key));
  if (unknown.length > 0) return `declares ${unknown.map((key) => `"${key}"`).join(', ')}, not a PipelineNodeKindSpec field`;
  if (!nonEmpty(kind.label) || !nonEmpty(kind.icon)) return 'needs a label and an icon name';
  if (typeof kind.category !== 'string' || !Object.hasOwn(PIPELINE_NODE_CATEGORIES, kind.category)) {
    return `has category "${String(kind.category)}", which is not a pipeline node category`;
  }
  if (!NODE_KIND_CLASSES.every((field) => nonEmpty(kind[field]))) return `needs ${NODE_KIND_CLASSES.join(', ')} classes`;
  if (typeof kind.hasInput !== 'boolean' || typeof kind.hasOutput !== 'boolean') return 'needs boolean hasInput and hasOutput';
  return undefined;
}

/** Validates every entry and flattens them; all violations are reported in one throw. */
export function composeWebExtensions(entries: readonly WebExtensionSlotEntry[]): ComposedWebExtensions {
  const violations: string[] = [];
  const owners = new Map<string, string>();
  const claim = (kind: string, key: string, label: string): boolean => {
    const owner = owners.get(`${kind}\0${key}`);
    if (owner !== undefined) {
      violations.push(`${kind} "${key}" is declared by ${owner} and ${label}`);
      return false;
    }
    owners.set(`${kind}\0${key}`, label);
    return true;
  };
  const components: Record<string, PlatformExtensionComponentLoader> = {};
  const routes: PlatformExtensionRoute[] = [];
  const windowablePages: PlatformExtensionWindowablePage[] = [];
  const connectorCatalog: PlatformExtensionConnectorCatalogEntry[] = [];
  const sidebar: PlatformExtensionSidebarItem[] = [];
  const pipelineNodeKinds: Record<string, PipelineNodeKindSpec> = {};

  for (const { name, web } of entries) {
    const label = `extension "${name}"`;
    if (!isObject(web)) {
      violations.push(`${label} does not default-export a PlatformExtensionWeb object`);
      continue;
    }
    for (const key of Object.keys(web)) {
      if (!Object.hasOwn(WEB_FIELDS, key)) violations.push(`${label} declares "${key}", which is not a PlatformExtensionWeb field`);
    }
    // A malformed field is the extension's defect, not an environment error: refuse it before reading it.
    const malformed = Object.entries(web).filter(([key, value]) => Object.hasOwn(WEB_FIELDS, key) && value !== undefined
      && (RECORD_FIELDS.has(key) ? !isObject(value) : !Array.isArray(value) || !value.every(isObject)));
    violations.push(...malformed.map(([key]) => `${label} "${key}" is not ${RECORD_FIELDS.has(key) ? 'an object' : 'a list of objects'}`));
    if (malformed.length > 0) continue;
    if ((web.pages ?? []).length > 0) violations.push(`${label} fills "pages": pages are declared in module manifests, not in the web slot`);

    const ownComponents = new Set<string>();
    for (const [key, loader] of Object.entries(web.components ?? {})) {
      ownComponents.add(key);
      if (typeof loader !== 'function') violations.push(`${label} component "${key}" is not a loader function`);
      else if (claim('component', key, label)) components[key] = loader;
    }
    // Route path → its requiredRole: the launcher entry and the sidebar link must read the same gate.
    const ownRoutes = new Map<string, string | undefined>();
    for (const route of web.routes ?? []) {
      ownRoutes.set(route.path, route.requiredRole);
      if (typeof route.path !== 'string' || !STATIC_ROUTE.test(route.path)) violations.push(`${label} route "${String(route.path)}" is not an absolute static path of lowercase kebab segments`);
      else if (!ownComponents.has(route.component)) violations.push(`${label} route "${route.path}" renders "${route.component}", which is not one of its components`);
      else if (route.requiredRole !== undefined && !nonEmpty(route.requiredRole)) violations.push(`${label} route "${route.path}" has an empty requiredRole`);
      else if (claim('route', route.path, label)) routes.push(route);
    }
    const gateDiffers = (path: string, requiredRole: string | undefined): boolean => ownRoutes.get(path) !== requiredRole;
    for (const page of web.windowablePages ?? []) {
      if (!ownComponents.has(page.pageKey)) violations.push(`${label} windowable page "${page.pageKey}" is not one of its components`);
      else if (!ownRoutes.has(page.pageRoute)) violations.push(`${label} windowable page "${page.pageKey}" points at "${page.pageRoute}", which is not one of its routes`);
      else if (gateDiffers(page.pageRoute, page.requiredRole)) violations.push(`${label} windowable page "${page.pageKey}" requiredRole differs from its route's`);
      else if (claim('windowable page', page.pageKey, label)) windowablePages.push(page);
    }
    for (const entry of web.connectorCatalog ?? []) {
      if (![entry.slug, entry.name, entry.description].every(nonEmpty)) violations.push(`${label} has a connector catalog entry without slug, name and description`);
      else if (claim('connector', entry.slug, label)) connectorCatalog.push(entry);
    }
    for (const item of web.sidebar ?? []) {
      if (!ownRoutes.has(item.path) || !nonEmpty(item.label)) violations.push(`${label} sidebar item "${item.path}" needs a label and one of its own routes`);
      else if (gateDiffers(item.path, item.requiredRole)) violations.push(`${label} sidebar item "${item.path}" requiredRole differs from its route's`);
      else if (claim('sidebar item', item.path, label)) sidebar.push(item);
    }
    for (const [type, spec] of Object.entries(web.pipelineNodeKinds ?? {})) {
      const defect = !PIPELINE_NODE_TYPE_NAME.test(type) ? `is not a node type name (${PIPELINE_NODE_TYPE_NAME.source})`
        : Object.hasOwn(Object.prototype, type) ? 'is an Object.prototype member name' : nodeKindDefect(spec);
      if (defect !== undefined) violations.push(`${label} pipeline node kind "${type}" ${defect}`);
      else if (claim('pipeline node kind', type, label)) pipelineNodeKinds[type] = spec;
    }
  }
  if (violations.length > 0) throw new PlatformWebExtensionError(violations);
  return { components, routes, windowablePages, connectorCatalog, sidebar, pipelineNodeKinds };
}

/**
 * Core first, then extensions; an extension key a core key matches refuses (never an override).
 * `matches` defaults to equality; routes pass a pattern match.
 */
export function joinCoreRoster<T>(
  kind: string,
  core: readonly T[],
  extension: readonly T[],
  keysOf: (item: T) => readonly string[],
  matches: (coreKey: string, key: string) => boolean = (coreKey, key) => coreKey === key,
): T[] {
  const coreKeys = core.flatMap(keysOf);
  const clashes = extension.flatMap(keysOf).filter((key) => coreKeys.some((coreKey) => matches(coreKey, key)));
  if (clashes.length > 0) throw new PlatformWebExtensionError(clashes.map((key) => `${kind} "${key}" is already a core ${kind}`));
  return [...core, ...extension];
}

export const webExtensions: ComposedWebExtensions = composeWebExtensions(extensions);
